package com.flowyride.feature.planner.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import com.flowyride.core.designsystem.theme.FlowyColors
import com.flowyride.core.domain.model.Place
import com.flowyride.feature.planner.PlannerAction
import com.flowyride.feature.planner.PlannerUiState
import com.flowyride.feature.planner.StopId
import com.flowyride.feature.planner.StopRole
import com.flowyride.feature.planner.StopUiState
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.add_stop
import com.flowyride.feature.planner.resources.arrival_label
import com.flowyride.feature.planner.resources.departure_label
import com.flowyride.feature.planner.resources.drag_stop
import com.flowyride.feature.planner.resources.label_my_position
import com.flowyride.feature.planner.resources.save_place
import com.flowyride.feature.planner.resources.move_down
import com.flowyride.feature.planner.resources.move_up
import com.flowyride.feature.planner.resources.remove_stop
import com.flowyride.feature.planner.resources.reverse_stops
import com.flowyride.feature.planner.resources.stop_label
import com.flowyride.feature.planner.resources.stop_options
import com.flowyride.feature.planner.stopRole
import org.jetbrains.compose.resources.stringResource

/**
 * Departure, stops and arrival. Rows are reordered by dragging their handle: the list is
 * reordered locally while dragging and a single [PlannerAction.MoveStop] is sent on release.
 */
@Composable
internal fun StopsSection(
    state: PlannerUiState,
    onAction: (PlannerAction) -> Unit,
    onSavePlace: (Place) -> Unit,
    modifier: Modifier = Modifier,
) {
    val currentOnAction by rememberUpdatedState(onAction)
    // Read from the long-lived drag gesture: must not be captured once.
    val currentIds by rememberUpdatedState(state.stops.map { it.id })
    val stopsById = state.stops.associateBy { it.id }
    val drag = remember { StopDragState() }
    // Visual order: the state's order, except while a row is being dragged.
    val order = drag.order.takeIf { drag.draggedId != null } ?: state.stops.map { it.id }

    Column(modifier) {
        order.forEachIndexed { index, id ->
            val stop = stopsById[id] ?: return@forEachIndexed
            key(id) {
                val isDragged = id == drag.draggedId
                StopRow(
                    stop = stop,
                    role = stopRole(index, order.size),
                    number = index,
                    canMoveUp = index > 0,
                    canMoveDown = index < order.lastIndex,
                    canRemove = state.canRemoveStops,
                    onAction = onAction,
                    onSavePlace = onSavePlace,
                    modifier = Modifier
                        .onSizeChanged { drag.heights[id] = it.height }
                        .zIndex(if (isDragged) 1f else 0f)
                        .graphicsLayer { translationY = if (isDragged) drag.offset else 0f }
                        .then(
                            if (isDragged) {
                                Modifier.shadow(8.dp, MaterialTheme.shapes.small)
                                    .background(MaterialTheme.colorScheme.surface, MaterialTheme.shapes.small)
                            } else {
                                Modifier
                            },
                        ),
                    dragHandle = Modifier.pointerInput(id) {
                        detectDragGestures(
                            onDragStart = { drag.start(id, currentIds) },
                            onDrag = { change, amount ->
                                change.consume()
                                drag.dragBy(amount.y)
                            },
                            onDragEnd = { drag.end()?.let { (stopId, to) -> currentOnAction(PlannerAction.MoveStop(stopId, to)) } },
                            onDragCancel = { drag.cancel() },
                        )
                    },
                )
            }
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            TextButton(onClick = { onAction(PlannerAction.AddStop) }, enabled = state.canAddStop) {
                Text("+ " + stringResource(Res.string.add_stop))
            }
            TextButton(onClick = { onAction(PlannerAction.ReverseStops) }) {
                Text("⇅ " + stringResource(Res.string.reverse_stops))
            }
        }
    }
}

/** Drag bookkeeping: which row, how far it moved, and the resulting visual order. */
private class StopDragState {
    var draggedId by mutableStateOf<StopId?>(null)
        private set
    var offset by mutableFloatStateOf(0f)
        private set
    var order by mutableStateOf(emptyList<StopId>())
        private set
    val heights = mutableStateMapOf<StopId, Int>()
    private var startIndex = -1

    fun start(id: StopId, currentOrder: List<StopId>) {
        order = currentOrder
        startIndex = currentOrder.indexOf(id)
        offset = 0f
        draggedId = id
    }

    /** Swaps with a neighbour once the dragged row covers half of it. */
    fun dragBy(dy: Float) {
        val id = draggedId ?: return
        offset += dy
        val index = order.indexOf(id)
        if (offset > 0 && index < order.lastIndex) {
            val next = heights[order[index + 1]] ?: return
            if (offset > next / 2f) {
                order = order.swapped(index, index + 1)
                offset -= next
            }
        } else if (offset < 0 && index > 0) {
            val previous = heights[order[index - 1]] ?: return
            if (-offset > previous / 2f) {
                order = order.swapped(index, index - 1)
                offset += previous
            }
        }
    }

    /** @return the moved stop and its new index, or null if it ended where it started. */
    fun end(): Pair<StopId, Int>? {
        val id = draggedId ?: return null
        val to = order.indexOf(id)
        cancel()
        return (id to to).takeIf { to != startIndex }
    }

    fun cancel() {
        draggedId = null
        offset = 0f
    }

    private fun List<StopId>.swapped(a: Int, b: Int) = toMutableList().also {
        val tmp = it[a]
        it[a] = it[b]
        it[b] = tmp
    }
}

@Composable
private fun StopRow(
    stop: StopUiState,
    role: StopRole,
    number: Int,
    canMoveUp: Boolean,
    canMoveDown: Boolean,
    canRemove: Boolean,
    onAction: (PlannerAction) -> Unit,
    onSavePlace: (Place) -> Unit,
    modifier: Modifier,
    dragHandle: Modifier,
) {
    val (roleLabel, color) = when (role) {
        StopRole.DEPARTURE -> stringResource(Res.string.departure_label) to FlowyColors.Departure
        StopRole.INTERMEDIATE -> stringResource(Res.string.stop_label, number) to FlowyColors.Stop
        StopRole.ARRIVAL -> stringResource(Res.string.arrival_label) to FlowyColors.Arrival
    }
    val label = if (stop.followsRider) stringResource(Res.string.label_my_position, roleLabel) else roleLabel
    val dragLabel = stringResource(Res.string.drag_stop)
    Row(
        modifier.padding(vertical = 2.dp),
        verticalAlignment = Alignment.Top,
    ) {
        Box(
            dragHandle
                .padding(top = 8.dp)
                .size(width = 24.dp, height = 48.dp)
                .semantics { contentDescription = dragLabel },
            contentAlignment = Alignment.Center,
        ) {
            Text("⠿", style = MaterialTheme.typography.titleLarge, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        PlaceSearchField(
            label = label,
            markerColor = color,
            state = stop,
            onQueryChange = { onAction(PlannerAction.QueryChanged(stop.id, it)) },
            onPlaceSelected = { onAction(PlannerAction.PlaceSelected(stop.id, it)) },
            onClear = { onAction(PlannerAction.FieldCleared(stop.id)) },
            modifier = Modifier.weight(1f),
        )
        StopMenu(
            canMoveUp = canMoveUp,
            canMoveDown = canMoveDown,
            canRemove = canRemove,
            onMove = { offset -> onAction(PlannerAction.MoveStop(stop.id, number + offset)) },
            onRemove = { onAction(PlannerAction.RemoveStop(stop.id)) },
            onSave = stop.selected?.takeIf { it.name.isNotEmpty() }?.let { place -> { onSavePlace(place) } },
        )
    }
}

@Composable
private fun StopMenu(
    canMoveUp: Boolean,
    canMoveDown: Boolean,
    canRemove: Boolean,
    onMove: (Int) -> Unit,
    onRemove: () -> Unit,
    onSave: (() -> Unit)?,
) {
    var expanded by remember { mutableStateOf(false) }
    val optionsLabel = stringResource(Res.string.stop_options)
    Box(Modifier.padding(top = 4.dp)) {
        IconButton(
            onClick = { expanded = true },
            modifier = Modifier.semantics { contentDescription = optionsLabel },
        ) {
            Text("⋮", style = MaterialTheme.typography.titleLarge)
        }
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            MenuItem(stringResource(Res.string.move_up), enabled = canMoveUp) { expanded = false; onMove(-1) }
            MenuItem(stringResource(Res.string.move_down), enabled = canMoveDown) { expanded = false; onMove(1) }
            MenuItem(stringResource(Res.string.save_place), enabled = onSave != null) { expanded = false; onSave?.invoke() }
            MenuItem(stringResource(Res.string.remove_stop), enabled = canRemove, color = MaterialTheme.colorScheme.error) {
                expanded = false
                onRemove()
            }
        }
    }
}

@Composable
private fun MenuItem(text: String, enabled: Boolean, color: Color = Color.Unspecified, onClick: () -> Unit) {
    DropdownMenuItem(
        text = { Text(text, color = if (enabled) color else Color.Unspecified) },
        onClick = onClick,
        enabled = enabled,
    )
}
